// SideNavBar.jsx
import React, { useEffect, useMemo } from 'react';
import {
	Box,
	Drawer,
	Toolbar,
	List,
	Typography,
	ListItem,
	IconButton,
	Stack,
	useMediaQuery,
	useTheme,
	Chip,
	Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useIsAuthenticated } from '@azure/msal-react';
import { SignInButton } from '@/components/buttons/SignInButton';
import { SignOutButton } from '@/components/buttons/SignOutButton';
import { InvertSideNavColorsButton } from '@/components/buttons/InvertSideNavColorsButton';
import { useDispatch, useSelector } from 'react-redux';
import { toggleOpen, toggleWidth } from '@/features/sideNav/sideNavSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { SideBarListItem } from './SideBarListItem';
import Hotjar from '@hotjar/browser';
import { ClientIcon } from '@/components/icons/Client';
import { TaskIcon } from '@/components/icons/TaskIcon';
import { NewBusinessIcon } from '@/components/icons/NewBusinessIcon';
import { ClaimsIcon } from '@/components/icons/ClaimsIcon';
import { EndorsementIcon } from '@/components/icons/Endorsement';
import { RenewalIcon } from '@/components/icons/Renewal';
import SettingsIcon from '@mui/icons-material/Settings';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { IS_DEV, IS_PRE, IS_PROD } from '@/utils/environment';
import { paths } from '@/app/routes';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import crmLogo from '@/assets/images/folioCRM.png';
import darkCrmLogo from '@/assets/images/folioCRM_dark_matched.png';
import { Email } from '@mui/icons-material';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import { RelationshipsIcon } from '@/components/icons/RelationshipsIcon';
import { AuditIcon } from '@/components/icons/AuditIcon';
import HistoryIcon from '@mui/icons-material/History';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FolioWorkflowLogo from '@/components/layouts/FolioWorkflow';
import WinbeatReverseLogo, { IntegratedWithWinbeat } from '@/components/layouts/WinbeatNextLogo';

const Header = ({ text }) => {
	const theme = useTheme();
	const { themeMode, invertSideNavColors } = useSelector((state) => state.userPrefs);
	const isLightMode = themeMode !== 'dark';
	const inverted = isLightMode && invertSideNavColors;
	const color = inverted ? theme.palette.sideNav : 'rgba(250, 253, 255, 0.8)';

	return (
		<Typography
			variant='subtitle2'
			sx={{
				color,
				marginLeft: '35px',
				paddingTop: '1.75em',
				fontSize: '0.75rem',
				paddingBottom: '8px',
				textTransform: 'uppercase',
				letterSpacing: '0.08em',
			}}
		>
			{text}
		</Typography>
	);
};

export const SideNavBar = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { open, width, isCollapsed } = useSelector((state) => state.sideNavSlice);
	const collapsed = isCollapsed && !isMobile;

	const { themeMode, invertSideNavColors } = useSelector((state) => state.userPrefs);
	const isLightMode = themeMode !== 'dark';
	const inverted = isLightMode && invertSideNavColors;

	const sideNavBg = inverted ? theme.palette.background.default : theme.palette.sideNav;
	const sideNavItemColor = inverted ? theme.palette.sideNav : 'whitesmoke';

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const isWinbeatLedger = userDetails?.isWinbeatLedger;

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProfile = Boolean(getFeatureFlags('profile')?.active);
	const saveEmailTemplates = Boolean(getFeatureFlags('saveEmailTemplates')?.active);
	const showUnlayerEmails = Boolean(getFeatureFlags('unlayerEmails')?.active);
	const showUnlayerDocuments = Boolean(getFeatureFlags('unlayerDocuments')?.active);
	const showResources = Boolean(getFeatureFlags('resources')?.active);
	const showDebtors = Boolean(getFeatureFlags('debtors')?.active);
	const showAutomations = Boolean(getFeatureFlags('displayAutomations')?.active);

	const handleDrawerToggle = () => dispatch(toggleOpen());
	const handleDrawerWidthToggle = () => {
		if (isMobile) {
			return handleDrawerToggle();
		}
		dispatch(toggleWidth());
	};

	const isAuthenticated = useIsAuthenticated();
	const { isAdminMode } = useAdminModeAccess();

	const adminList = useMemo(
		() => [
			{
				text: 'Users',
				icon: <GroupOutlinedIcon sx={{ fontSize: 30 }} />,
				link: paths.adminUsers,
			},
			// {
			// 	text: 'Networks',
			// 	icon: <HubOutlinedIcon sx={{ fontSize: 30 }} />,
			// 	link: paths.adminNetworks,
			// },
			{
				text: 'Brokers',
				icon: <BusinessOutlinedIcon sx={{ fontSize: 30 }} />,
				link: paths.adminBrokers,
			},
			{
				text: 'Clients',
				icon: (
					<ClientIcon
						width='30px'
						height='30px'
						bgColor='transparent'
						sx={{ ml: '-3px' }}
					/>
				),
				link: paths.adminClients,
			},
			{
				text: 'Feature Flags',
				icon: <FlagOutlinedIcon sx={{ fontSize: 30 }} />,
				link: paths.adminFeatureFlags,
			},
		],
		[],
	);

	const homeList = useMemo(() => {
		const list = [
			{
				text: 'Clients',
				icon: (
					<ClientIcon
						width='30px'
						height='30px'
						bgColor='transparent'
						sx={{ ml: '-3px' }}
					/>
				),
				link: paths.home,
			},
			{
				text: 'Prospects',
				icon: <ContactPageIcon width='30px' height='30px' />,
				link: paths.prospects,
			},
			{
				text: 'Relationships',
				icon: (
					<RelationshipsIcon
						width='30px'
						height='30px'
						bgColor='transparent'
					/>
				),
				link: paths.relationships,
			},
			{
				text: 'Tasks',
				icon: <TaskIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.tasks,
			},
			{
				text: 'Audit',
				icon: <AuditIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.audit,
			},
		];
		return isAuthenticated ? list : list.slice(0, -2);
	}, [isAuthenticated, userDetails]);

	const pipelinesList = useMemo(() => {
		const list = [
			{
				text: 'New Business',
				icon: <NewBusinessIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.newBusiness,
			},
			{
				text: 'Claims',
				icon: <ClaimsIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.claims,
			},
			{
				text: 'Endorsements',
				icon: <EndorsementIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.endorsements,
			},
			{
				text: 'Renewals',
				icon: <RenewalIcon width='30px' height='30px' bgColor='transparent' />,
				link: paths.renewals,
			},
			...(showDebtors
				? [{ text: 'Debtors', icon: <PaidOutlinedIcon />, link: paths.debtors }]
				: []),
		];

		return list;
	}, [showDebtors, sideNavItemColor]);

	const myAccountList = useMemo(() => {
		if (!isAuthenticated) return [];
		return [
			...(showProfile
				? [
						{
							text: 'Settings',
							icon: <SettingsIcon width='30px' height='30px' />,
							link: paths.settings,
						},
					]
				: []),
			...(saveEmailTemplates || showUnlayerEmails || showUnlayerDocuments
				? [
						...(!showUnlayerEmails && saveEmailTemplates
							? [
									{
										text: 'Templates',
										icon: <Email width='30px' height='30px' />,
										link: paths.templates,
									},
								]
							: []),
						...(showUnlayerEmails
							? [
									{
										text: 'Templates',
										icon: <Email width='30px' height='30px' />,
										link: paths.unlayerTemplates,
									},
								]
							: []),
						...(showUnlayerDocuments
							? [
									{
										text: 'Document Templates',
										icon: <ArticleOutlinedIcon width='30px' height='30px' />,
										link: paths.unlayerDocumentTemplates,
									},
								]
							: []),
					]
				: []),
			...(showAutomations
				? [
						{
							text: 'Automations',
							icon: <AutoModeIcon width='30px' height='30px' />,
							link: paths.automations,
						},
					]
				: []),
		];
	}, [
		isAuthenticated,
		showProfile,
		saveEmailTemplates,
		showUnlayerEmails,
		showUnlayerDocuments,
		showAutomations,
	]);

	const myResourcesList = useMemo(() => {
		if (!isAuthenticated) return [];
		return [
			{
				text: 'Contact Support',
				icon: <InfoIcon width='30px' height='30px' />,
				external: false,
				onClick: () => {
					if (IS_PROD) {
						Hotjar.event('report_issue_clicked');
					}
					dispatch(openDialog({ dialogName: 'issueReporting' }));
				},
			},
		];
	}, [dispatch, isAuthenticated, showResources]);

	const displayList = (list, title) =>
		list.map((item, index) => (
			<SideBarListItem
				key={`side-bar-list-item-${title}-${index}-${item.text}`}
				item={item}
				color={sideNavItemColor}
			/>
		));

	return (
		<>
			<Drawer
				className='side_nav_bar'
				sx={{
					width: width,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: width,
						boxSizing: 'border-box',
						border: 'none',
						backgroundColor: sideNavBg,
						overflowX: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						transition: theme.transitions.create(['width'], {
							easing: theme.transitions.easing.sharp,
							duration: theme.transitions.duration.leavingScreen,
						}),
						...(inverted && {
							borderRight: `1px solid ${theme.palette.divider}`,
						}),
					},
					'& .MuiListItem-root': {
						padding: collapsed ? 0 : '0 16px',
					},
				}}
				variant={isMobile ? 'temporary' : 'permanent'}
				open={isMobile ? open : true}
				onClose={handleDrawerToggle}
				anchor='left'
			>
				{/* <Box
					sx={{
						height: '48px',
					}}
				/> */}
				{collapsed ? (
					<IconButton
						variant='outlined'
						onClick={handleDrawerWidthToggle}
						sx={{ mb: '0.5em' }}
					>
						<MenuIcon sx={{ color: sideNavItemColor }} />
					</IconButton>
				) : (
					<Box sx={{ ml: '20px', mb: '0.5em', mr: '20px' }}>
						<Stack
							direction='row'
							justifyContent={'space-between'}
							alignContent={'start'}
							spacing={0.5}
							width='100%'
							pt={1.25}
							pb={1.5}
						>
							<Stack
								direction='row'
								alignItems='center'
								spacing={1}
								justifyContent={'space-between'}
								width='100%'
							>
								{isAdminMode ? (
									<Typography
										sx={{
											color: sideNavItemColor,
											fontWeight: 700,
											fontSize: '1rem',
											lineHeight: '22px',
											whiteSpace: 'nowrap',
										}}
									>
										FolioAdmin
									</Typography>
								) : isWinbeatLedger ? (
									<FolioWorkflowLogo
										height={IS_DEV || IS_PRE ? 18 : 22}
										variant={inverted ? 'light' : 'dark'}
									/>
								) : (
									<Box
										component='img'
										src={inverted ? darkCrmLogo : crmLogo}
										alt='CRM Logo'
										sx={{
											height: 22,
											width: 'auto',
											display: 'block',
										}}
									/>
								)}
								{IS_DEV && <Chip label='DEV' color='primary' size='small' />}
								{IS_PRE && <Chip label='PRE' color='secondary' size='small' />}
							</Stack>
							<IconButton
								variant='outlined'
								onClick={handleDrawerWidthToggle}
								sx={{ p: '4px' }}
								// sx={{ pt: '0.1em' }}
							>
								<KeyboardArrowLeftIcon sx={{ color: sideNavItemColor }} />
							</IconButton>
						</Stack>
						{isWinbeatLedger && !isAdminMode && (
							<IntegratedWithWinbeat variant={inverted ? 'light' : 'dark'} />
						)}
					</Box>
				)}

				<Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
					{isAdminMode ? (
						<List disablePadding>{displayList(adminList, 'admin')}</List>
					) : (
						<>
							<List disablePadding>{displayList(homeList, 'home')}</List>
							{isAuthenticated && (
								<List disablePadding>
									{!collapsed && <Header text='Pipelines' />}
									{displayList(pipelinesList, 'pipelines')}
								</List>
							)}
							{showResources && (
								<List disablePadding>
									{!collapsed && <Header text='Resources' />}
									{isAuthenticated && displayList(myResourcesList, 'my-resources')}
								</List>
							)}
							<List disablePadding>
								{!collapsed && <Header text='Account' />}
								{isAuthenticated && displayList(myAccountList, 'my-account')}
							</List>
						</>
					)}
				</Box>

				{isLightMode && (
					<Box sx={{ mx: collapsed ? 0 : '24px', my: '4px' }}>
						<InvertSideNavColorsButton color={sideNavItemColor} />
					</Box>
				)}

				<Divider
					sx={{
						borderColor: inverted
							? 'rgba(28, 37, 54, 0.12)'
							: 'rgba(250, 253, 255, 0.12)',
					}}
				/>
				<List disablePadding sx={{ flexShrink: 0 }}>
					<ListItem disablePadding disableGutters={collapsed}>
						{isAuthenticated ? (
							<SignOutButton color={sideNavItemColor} />
						) : (
							<SignInButton showListItem color={sideNavItemColor} />
						)}
					</ListItem>
				</List>
			</Drawer>
		</>
	);
};
